export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  memberSince: string;
  vipLevel: string;
  rewardPoints: number;
  avatarUrl?: string;
  recentActivity: Array<{ id: string; action: string; time: string }>;
}

export interface UserAddress {
  id: string;
  type: 'Shipping' | 'Billing';
  isDefault: boolean;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface PaymentMethodItem {
  id: string;
  type: 'Card' | 'UPI' | 'Wallet';
  brand?: string;
  last4?: string;
  expiry?: string;
  upiId?: string;
  walletName?: string;
  isDefault: boolean;
}

export interface NotificationSettings {
  orderUpdates: boolean;
  offers: boolean;
  backInStock: boolean;
  newsletter: boolean;
}

const EMPTY_USER: UserProfile = {
  id: '',
  firstName: '',
  lastName: '',
  name: '',
  email: '',
  phone: '',
  memberSince: '',
  vipLevel: 'Guest Patron',
  rewardPoints: 0,
  recentActivity: [],
};

const DEFAULT_ADDRESSES: UserAddress[] = [];

const DEFAULT_PAYMENTS: PaymentMethodItem[] = [];

const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  orderUpdates: true,
  offers: false,
  backInStock: false,
  newsletter: false,
};

type Listener = () => void;

class AuthServiceClass {
  private isLoggedInState: boolean = false;
  private userState: UserProfile = EMPTY_USER;
  private addressesState: UserAddress[] = [];
  private paymentMethodsState: PaymentMethodItem[] = [];
  private notificationsState: NotificationSettings = DEFAULT_NOTIFICATIONS;
  private wishlistState: string[] = [];
  private listeners: Listener[] = [];

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    if (typeof window === 'undefined') return;
    try {
      const storedAuth = localStorage.getItem('noor_auth_logged_in');
      if (storedAuth === 'true') {
        const storedUser = localStorage.getItem('noor_auth_user');
        if (storedUser) {
          this.userState = JSON.parse(storedUser);
          this.isLoggedInState = true;
        } else {
          this.isLoggedInState = false;
          this.userState = EMPTY_USER;
        }
      } else {
        this.isLoggedInState = false;
        this.userState = EMPTY_USER;
      }

      const storedAddresses = localStorage.getItem('noor_auth_addresses');
      if (storedAddresses) {
        this.addressesState = JSON.parse(storedAddresses);
      }
      const storedPayments = localStorage.getItem('noor_auth_payments');
      if (storedPayments) {
        this.paymentMethodsState = JSON.parse(storedPayments);
      }
      const storedNotifs = localStorage.getItem('noor_auth_notifications');
      if (storedNotifs) {
        this.notificationsState = JSON.parse(storedNotifs);
      }
      const storedWishlist = localStorage.getItem('noor_auth_wishlist');
      if (storedWishlist) {
        this.wishlistState = JSON.parse(storedWishlist);
      }
    } catch (e) {
      console.warn('Could not load auth data from storage', e);
      this.isLoggedInState = false;
      this.userState = EMPTY_USER;
    }
  }

  private saveToStorage() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('noor_auth_logged_in', String(this.isLoggedInState));
      localStorage.setItem('noor_auth_user', JSON.stringify(this.userState));
      localStorage.setItem('noor_auth_addresses', JSON.stringify(this.addressesState));
      localStorage.setItem('noor_auth_payments', JSON.stringify(this.paymentMethodsState));
      localStorage.setItem('noor_auth_notifications', JSON.stringify(this.notificationsState));
      localStorage.setItem('noor_auth_wishlist', JSON.stringify(this.wishlistState));
    } catch (e) {
      console.warn('Could not save auth data to storage', e);
    }
  }

  public notify() {
    this.saveToStorage();
    this.listeners.forEach((l) => l());
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // Getters
  public isLoggedIn(): boolean {
    return this.isLoggedInState;
  }

  public getUser(): UserProfile {
    return this.userState;
  }

  public getAddresses(): UserAddress[] {
    return this.addressesState;
  }

  public getPaymentMethods(): PaymentMethodItem[] {
    return this.paymentMethodsState;
  }

  public getNotifications(): NotificationSettings {
    return this.notificationsState;
  }

  public getWishlist(): string[] {
    return this.wishlistState;
  }

  // Actions
  public login(email: string, firstName?: string, lastName?: string) {
    this.isLoggedInState = true;
    const cleanFirstName = firstName || email.split('@')[0] || 'Patron';
    const cleanLastName = lastName || 'Member';
    this.userState = {
      ...this.userState,
      email,
      firstName: cleanFirstName,
      lastName: cleanLastName,
      name: cleanFirstName,
    };
    this.notify();
  }

  public logout() {
    this.isLoggedInState = false;
    this.userState = EMPTY_USER;
    this.addressesState = [];
    this.paymentMethodsState = [];
    this.wishlistState = [];
    if (typeof window !== 'undefined') {
      localStorage.removeItem('noor_auth_logged_in');
      localStorage.removeItem('noor_auth_user');
      localStorage.removeItem('noor_auth_addresses');
      localStorage.removeItem('noor_auth_payments');
      localStorage.removeItem('noor_auth_notifications');
      localStorage.removeItem('noor_auth_wishlist');
      localStorage.removeItem('noor_cart_session');
    }
    this.notify();
  }

  public updateUser(updated: Partial<UserProfile>) {
    this.userState = { ...this.userState, ...updated };
    if (updated.firstName) {
      this.userState.name = updated.firstName;
    }
    this.notify();
  }

  public updateProfile(updated: Partial<UserProfile>) {
    this.updateUser(updated);
  }

  public addAddress(address: Omit<UserAddress, 'id'>) {
    const newAddr: UserAddress = {
      ...address,
      id: `addr-${Date.now()}`,
    };
    if (newAddr.isDefault) {
      this.addressesState = this.addressesState.map((a) => ({ ...a, isDefault: false }));
    }
    this.addressesState.push(newAddr);
    this.notify();
  }

  public setDefaultAddress(id: string) {
    this.addressesState = this.addressesState.map((a) => ({
      ...a,
      isDefault: a.id === id,
    }));
    this.notify();
  }

  public removeAddress(id: string) {
    this.addressesState = this.addressesState.filter((a) => a.id !== id);
    this.notify();
  }

  public addPaymentMethod(pm: Omit<PaymentMethodItem, 'id'>) {
    const newPm: PaymentMethodItem = {
      ...pm,
      id: `pm-${Date.now()}`,
    };
    if (newPm.isDefault) {
      this.paymentMethodsState = this.paymentMethodsState.map((p) => ({ ...p, isDefault: false }));
    }
    this.paymentMethodsState.push(newPm);
    this.notify();
  }

  public setDefaultPaymentMethod(id: string) {
    this.paymentMethodsState = this.paymentMethodsState.map((p) => ({
      ...p,
      isDefault: p.id === id,
    }));
    this.notify();
  }

  public removePaymentMethod(id: string) {
    this.paymentMethodsState = this.paymentMethodsState.filter((p) => p.id !== id);
    this.notify();
  }

  public updateNotifications(settings: Partial<NotificationSettings>) {
    this.notificationsState = { ...this.notificationsState, ...settings };
    this.notify();
  }

  public toggleWishlist(productId: string) {
    if (this.wishlistState.includes(productId)) {
      this.wishlistState = this.wishlistState.filter((id) => id !== productId);
    } else {
      this.wishlistState.push(productId);
    }
    this.notify();
  }
}

export const authService = new AuthServiceClass();
